// Device Detection Utility
// Provides functions to detect user's device type and redirect to appropriate store

export type DeviceType = 'android' | 'ios' | 'desktop' | 'unknown';

export interface StoreUrls {
  android: string;
  ios: string;
  fallback: string;
}

/**
 * Detects the user's device type based on user agent
 * @returns DeviceType - 'android', 'ios', 'desktop', or 'unknown'
 */
export const detectDeviceType = (): DeviceType => {
  if (typeof window === 'undefined') {
    return 'unknown';
  }

  const userAgent = window.navigator.userAgent.toLowerCase();
  
  // Check for Android devices
  if (/android/.test(userAgent)) {
    return 'android';
  }
  
  // Check for iOS devices (iPhone, iPad, iPod)
  if (/iphone|ipad|ipod/.test(userAgent)) {
    return 'ios';
  }
  
  // Check for desktop browsers
  if (/windows|macintosh|linux/.test(userAgent) && !/mobile|android|iphone|ipad|ipod/.test(userAgent)) {
    return 'desktop';
  }
  
  return 'unknown';
};

/**
 * Gets the appropriate store URL based on device type
 * @param deviceType - The detected device type
 * @param storeUrls - Custom store URLs (optional)
 * @returns The appropriate store URL
 */
export const getStoreUrl = (deviceType: DeviceType, storeUrls?: Partial<StoreUrls>): string => {
  const defaultUrls: StoreUrls = {
    android: 'https://play.google.com/store/apps/details?id=com.yohavb.rushhour',
    ios: 'https://apps.apple.com/app/rushhour-parking-jam-solution/id6754037494',
    fallback: '/' // Redirect to home page
  };

  const urls = { ...defaultUrls, ...storeUrls };

  switch (deviceType) {
    case 'android':
      return urls.android;
    case 'ios':
      return urls.ios;
    case 'desktop':
    case 'unknown':
    default:
      return urls.fallback;
  }
};

/**
 * Redirects user to the appropriate store based on their device
 * @param storeUrls - Custom store URLs (optional)
 */
export const redirectToStore = (storeUrls?: Partial<StoreUrls>): void => {
  const deviceType = detectDeviceType();
  const storeUrl = getStoreUrl(deviceType, storeUrls);
  
  // Always use window.location.href for fastest redirect
  window.location.href = storeUrl;
};
